import { AvatarWithVolume } from "@/components/pieces/users/AvatarWithVolume";
import { useContainerDimensions } from "@/hooks/use-container-dimensions";

export function AvatarBox(props: {
  name: string | undefined | null;
  photo: string | undefined | null;
  backgroundColor: string;
  volumeLevel: number;
  isPresenting: boolean;
}) {
  const container = useContainerDimensions();
  const width = container.dimensions.width;

  const size = width * getAvatarSizePercentage(width);

  return (
    <div
      ref={container.setContainerRef}
      className="grid h-full rounded-sm w-full"
      style={{
        backgroundColor: props.backgroundColor,
        border: props.volumeLevel > 0 ? "2px solid #1C85F5" : undefined,
      }}
    >
      <div className="mx-4 self-center" style={{ gridArea: "1/1" }}>
        <AvatarWithVolume
          user={{
            name: props.name ?? undefined,
            photo: props.photo ?? undefined,
          }}
          size={size}
          volumeLevel={props.volumeLevel}
        />
      </div>
      <div
        className="grid py-1 px-2 opacity-60 text-xs overflow-hidden text-white bg-gray-700 font-semibold rounded-[36px] m-2 justify-self-start self-end"
        style={{ gridArea: "1/1" }}
      >
        <div className="truncate">{props.name}</div>
      </div>
    </div>
  );
}

/**
 * Calculates responsive avatar size ratio based on container width using an **INVERSE**
 * relationship:
 * - larger containers use smaller percentages
 * - smaller containers use larger percentages.
 *
 * Initially we used a fixed percentage, but such fixation will
 * either cause:
 *
 * (1) avatar photo pixelation on large screens
 *
 * or
 *
 * (2) poor visibility of avatar on small screens.
 */
const getAvatarSizePercentage = (width: number) => {
  // The bound 200 and 700 are chosen based on the typical width of the avatar box
  const minWidth = 200;
  const maxWidth = 400;

  // Clamp width to bounds
  const clampedWidth = Math.max(minWidth, Math.min(maxWidth, width));

  // Linear interpolation: as width increases, ratio decreases
  const ratio =
    0.5 - ((clampedWidth - minWidth) / (maxWidth - minWidth)) * 0.25;

  return ratio;
};
